import { createContext, useContext, type ReactNode } from 'react';
import { ROLE_TYPES } from '@scio3/shared-types';
import { useAuthStore } from '@/stores/auth-store';
import { useTeamStore } from '@/stores/team-store';

/**
 * Group Security v3 §12 — HOME PATH (Root, path=null) action lock.
 *
 * The backend blocks a NON-admin GROUP member with `canEditHomePath=false` from creating OR editing
 * loose items at the section Root (path=null) — see open-api principal.service.__pe. This context lets
 * the FE MIRROR that: a content page (Assets/Screens/Playlists/Schedules/Engage) wraps its subtree in
 * `RootActionLockProvider` with the computed lock, and `usePermissions` reads it — so every
 * `canCreate/canEdit/canDelete` consumer under that page (toolbars, item menus, bulk bars) is disabled
 * at Root with no per-site wiring. Non-content pages never provide the context ⇒ default `false` ⇒ no
 * effect. Admins (Owner/Admin/Super Admin/Team Owner) and non-group members are never locked.
 */
const RootActionLockContext = createContext(false);

const ADMIN_ROLES: string[] = [
  ROLE_TYPES.ADMIN,
  ROLE_TYPES.SUPER_ADMIN,
  ROLE_TYPES.OWNER,
  ROLE_TYPES.TEAM_OWNER,
];

export function RootActionLockProvider({
  locked,
  children,
}: {
  locked: boolean;
  children: ReactNode;
}) {
  return <RootActionLockContext.Provider value={locked}>{children}</RootActionLockContext.Provider>;
}

/** Whether the current `usePermissions` consumer sits under a Root-locked content subtree. */
export function useRootActionLock(): boolean {
  return useContext(RootActionLockContext);
}

/**
 * Compute the Root (path=null) action lock for the CURRENT member — the value a content page passes to
 * `RootActionLockProvider`. Mirrors the backend §12 gate exactly: locked ⇔ at Root AND a non-admin
 * GROUP member (has group grants) AND `canEditHomePath !== true`. `atRoot` = the page is showing the
 * section Root (no folder open, path=null).
 */
export function useRootActionLockValue(atRoot: boolean): boolean {
  const me = useAuthStore((s) => s.me);
  const activeTeamId = useTeamStore((s) => s.activeTeamId);
  if (!atRoot || !me) return false;
  const teamRole = me.teams?.find((t) => t._id === activeTeamId)?.role;
  if (ADMIN_ROLES.includes(teamRole ?? '')) return false; // Owner/Admin/Super Admin/Team Owner bypass
  const isGroupMember = (me.groupIds?.length ?? 0) > 0;
  if (!isGroupMember) return false; // backend only locks group members at Root
  return me.canEditHomePath !== true;
}
